// Mounted by PageFrame for authenticated app pages and directly by
// unauthenticated bare-Frame pages (logins, errors, magic-link verify).
// Not subject to the per-layout Biome restriction — its valid call sites
// are contextual.

import { usePage } from "@inertiajs/react";
import type { AlertStatus } from "@ui/components/Alert";
import { useCallback, useMemo, useState } from "react";
import { isFlashMessage, normalizeFlashValue, type SharedProps } from "@/types";

export interface FlashAlert {
  description: string | string[];
  id: string;
  status: AlertStatus;
  title: string;
}

// Rails producers aren't statically constrained to AlertStatus, so an
// out-of-range status falls back to "error" at runtime.
const ALERT_STATUSES: Set<AlertStatus> = new Set(["success", "error", "warning", "info"]);

export function useFlashAlerts() {
  const { flash } = usePage<SharedProps>().props;
  const fingerprint = useMemo(() => JSON.stringify(flash.alert), [flash.alert]);
  const [dismissed, setDismissed] = useState<string | null>(null);

  const alerts = useMemo<FlashAlert[]>(() => {
    const alertValue = flash.alert;
    const normalized = normalizeFlashValue(alertValue);
    if (!normalized) return [];
    if (dismissed === fingerprint) return [];

    const isMessage = isFlashMessage(alertValue);
    const description = isMessage && alertValue.list?.length ? alertValue.list : normalized.description;
    const status =
      isMessage && ALERT_STATUSES.has(alertValue.status as AlertStatus) ? (alertValue.status as AlertStatus) : "error";

    return [{ id: fingerprint, title: normalized.title, description, status }];
  }, [flash.alert, fingerprint, dismissed]);

  const dismissAlert = useCallback(() => {
    setDismissed(fingerprint);
  }, [fingerprint]);

  return { alerts, dismissAlert };
}
